import React, {
    useCallback,
    useEffect,
    useId,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import './DatePicker.css';

/** Local calendar date → YYYY-MM-DD (no UTC shift). */
export function formatISODate(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function parseISODate(str) {
    if (!str || !/^\d{4}-\d{2}-\d{2}$/.test(str)) return null;
    const [y, mo, d] = str.split('-').map(Number);
    return new Date(y, mo - 1, d);
}

function buildMonthCells(year, month, weekStartsOn) {
    const first = new Date(year, month, 1);
    const offset = (first.getDay() - weekStartsOn + 7) % 7;
    const daysIn = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < offset; i += 1) cells.push({ type: 'pad' });
    for (let day = 1; day <= daysIn; day += 1) {
        const iso = formatISODate(new Date(year, month, day));
        cells.push({ type: 'day', day, iso });
    }
    while (cells.length % 7 !== 0) cells.push({ type: 'pad' });
    return cells;
}

function getWeekStartsOn(locale) {
    try {
        const loc = new Intl.Locale(locale);
        const wi = loc.weekInfo;
        if (wi && typeof wi.firstDay === 'number') {
            return wi.firstDay % 7;
        }
    } catch {
        /* ignore */
    }
    return 0;
}

/**
 * DatePicker — accessible date field with calendar popover (YYYY-MM-DD).
 */
function DatePicker({
    id: idProp,
    name = 'date',
    value,
    onChange,
    minDate,
    locale,
    ariaLabelledBy,
    disabled = false,
    required = false,
    dir = 'ltr',
    className = '',
}) {
    const { t } = useTranslation();
    const reactId = useId();
    const triggerId = idProp ?? `${reactId}-trigger`;
    const dialogId = `${triggerId}-dialog`;

    const triggerRef = useRef(null);
    const popoverRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0, width: 280 });

    const effectiveLocale = locale || 'en';
    const weekStartsOn = useMemo(() => getWeekStartsOn(effectiveLocale), [effectiveLocale]);

    const min = minDate || formatISODate(new Date());
    const minParsed = useMemo(() => parseISODate(min), [min]);

    const initialView = useMemo(() => {
        const fromValue = parseISODate(value);
        if (fromValue) {
            if (minParsed && fromValue < minParsed) {
                return { y: minParsed.getFullYear(), m: minParsed.getMonth() };
            }
            return { y: fromValue.getFullYear(), m: fromValue.getMonth() };
        }
        if (minParsed) {
            return { y: minParsed.getFullYear(), m: minParsed.getMonth() };
        }
        const n = new Date();
        return { y: n.getFullYear(), m: n.getMonth() };
    }, [value, minParsed]);

    const [viewYear, setViewYear] = useState(initialView.y);
    const [viewMonth, setViewMonth] = useState(initialView.m);

    useEffect(() => {
        setViewYear(initialView.y);
        setViewMonth(initialView.m);
    }, [initialView.y, initialView.m]);

    const todayIso = formatISODate(new Date());

    const weekDayLabels = useMemo(() => {
        const sun = new Date(2024, 0, 7);
        return Array.from({ length: 7 }, (_, i) => {
            const d = new Date(sun);
            d.setDate(sun.getDate() + i);
            return d.toLocaleDateString(effectiveLocale, { weekday: 'narrow' });
        });
    }, [effectiveLocale]);

    const orderedWeekDays = useMemo(() => {
        const arr = [...weekDayLabels];
        return [...arr.slice(weekStartsOn), ...arr.slice(0, weekStartsOn)];
    }, [weekDayLabels, weekStartsOn]);

    const cells = useMemo(
        () => buildMonthCells(viewYear, viewMonth, weekStartsOn),
        [viewYear, viewMonth, weekStartsOn]
    );

    const monthTitle = useMemo(
        () =>
            new Date(viewYear, viewMonth, 1).toLocaleDateString(effectiveLocale, {
                month: 'long',
                year: 'numeric',
            }),
        [viewYear, viewMonth, effectiveLocale]
    );

    const canPrevMonth = useMemo(() => {
        if (!minParsed) return true;
        const vy = minParsed.getFullYear();
        const vm = minParsed.getMonth();
        return viewYear > vy || (viewYear === vy && viewMonth > vm);
    }, [minParsed, viewYear, viewMonth]);

    const goPrev = () => {
        if (!canPrevMonth) return;
        setViewMonth((m) => {
            if (m === 0) {
                setViewYear((y) => y - 1);
                return 11;
            }
            return m - 1;
        });
    };

    const goNext = () => {
        setViewMonth((m) => {
            if (m === 11) {
                setViewYear((y) => y + 1);
                return 0;
            }
            return m + 1;
        });
    };

    const updatePosition = useCallback(() => {
        const el = triggerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const w = Math.max(rect.width, 300);
        const isRtl = dir === 'rtl';
        let left = isRtl ? rect.right - w : rect.left;
        left = Math.max(8, Math.min(left, window.innerWidth - w - 8));
        setCoords({
            top: rect.bottom + 8,
            left,
            width: w,
        });
    }, [dir]);

    useLayoutEffect(() => {
        if (!open) return;
        updatePosition();
    }, [open, updatePosition, viewYear, viewMonth]);

    useEffect(() => {
        if (!open) return;
        const onScroll = () => updatePosition();
        const onResize = () => updatePosition();
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onResize);
        };
    }, [open, updatePosition]);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            const t = e.target;
            if (
                triggerRef.current?.contains(t) ||
                popoverRef.current?.contains(t)
            ) {
                return;
            }
            setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open]);

    const displayText = useMemo(() => {
        if (!value) return '';
        const d = parseISODate(value);
        if (!d) return value;
        return d.toLocaleDateString(effectiveLocale, { dateStyle: 'long' });
    }, [value, effectiveLocale]);

    const selectDay = (iso) => {
        if (iso < min) return;
        onChange(iso);
        setOpen(false);
    };

    const toggleOpen = () => {
        if (disabled) return;
        setOpen((o) => !o);
    };

    const rootClass = ['rdp', className].filter(Boolean).join(' ');

    const popover = open
        ? createPortal(
              <div
                  ref={popoverRef}
                  id={dialogId}
                  className="rdp-popover"
                  role="dialog"
                  aria-modal="false"
                  aria-label={t('reservation.date')}
                  dir={dir}
                  style={{
                      position: 'fixed',
                      top: coords.top,
                      left: coords.left,
                      width: coords.width,
                      zIndex: 10050,
                  }}
              >
                  <div className="rdp-popover-head">
                      <button
                          type="button"
                          className="rdp-nav"
                          onClick={goPrev}
                          disabled={!canPrevMonth}
                          aria-label={t('reservation.datePickerPrevMonth')}
                      >
                          {dir === 'rtl' ? (
                              <ChevronRight size={20} strokeWidth={2} />
                          ) : (
                              <ChevronLeft size={20} strokeWidth={2} />
                          )}
                      </button>
                      <span className="rdp-month-title" aria-live="polite">
                          {monthTitle}
                      </span>
                      <button
                          type="button"
                          className="rdp-nav"
                          onClick={goNext}
                          aria-label={t('reservation.datePickerNextMonth')}
                      >
                          {dir === 'rtl' ? (
                              <ChevronLeft size={20} strokeWidth={2} />
                          ) : (
                              <ChevronRight size={20} strokeWidth={2} />
                          )}
                      </button>
                  </div>
                  <div className="rdp-weekdays" aria-hidden="true">
                      {orderedWeekDays.map((wd, i) => (
                          <span key={i} className="rdp-weekday">
                              {wd}
                          </span>
                      ))}
                  </div>
                  <div className="rdp-grid" role="grid">
                      {cells.map((cell, idx) => {
                          if (cell.type === 'pad') {
                              return <span key={`p-${idx}`} className="rdp-cell--pad" />;
                          }
                          const disabledDay = cell.iso < min;
                          const selected = value === cell.iso;
                          const isToday = cell.iso === todayIso;
                          return (
                              <button
                                  key={cell.iso}
                                  type="button"
                                  role="gridcell"
                                  disabled={disabledDay}
                                  className={[
                                      'rdp-cell--day',
                                      selected && 'rdp-cell--selected',
                                      isToday && 'rdp-cell--today',
                                      disabledDay && 'rdp-cell--disabled',
                                  ]
                                      .filter(Boolean)
                                      .join(' ')}
                                  onClick={() => selectDay(cell.iso)}
                                  aria-selected={selected}
                                  aria-current={isToday ? 'date' : undefined}
                              >
                                  {cell.day}
                              </button>
                          );
                      })}
                  </div>
              </div>,
              document.body
          )
        : null;

    return (
        <div className={rootClass}>
            <input
                type="text"
                name={name}
                value={value || ''}
                readOnly
                required={required}
                disabled={disabled}
                tabIndex={-1}
                aria-hidden="true"
                className="rdp-sr-only"
            />
            <button
                ref={triggerRef}
                type="button"
                id={triggerId}
                className={`rdp-trigger ${open ? 'rdp-trigger--open' : ''}`}
                onClick={toggleOpen}
                disabled={disabled}
                aria-expanded={open}
                aria-haspopup="dialog"
                aria-controls={open ? dialogId : undefined}
                aria-labelledby={ariaLabelledBy || undefined}
                aria-label={!ariaLabelledBy ? t('reservation.datePickerOpenCalendar') : undefined}
            >
                <span className={value ? 'rdp-trigger-text' : 'rdp-trigger-text rdp-trigger-text--placeholder'}>
                    {value ? displayText : t('reservation.datePickerPlaceholder')}
                </span>
                <span className="rdp-trigger-ico" aria-hidden>
                    <ChevronDown size={18} strokeWidth={2} />
                </span>
            </button>
            {popover}
        </div>
    );
}

export default DatePicker;
